import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Eye,
  EyeOff,
  Shield,
  Users,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  KeyRound,
  Mail,
} from "lucide-react";
import { BrandLogo } from "@/projects/components/brand-logo";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { Checkbox } from "@/projects/components/ui/checkbox";
import { setAuth, getTeachers } from "@/projects/lib/store";
import { toast } from "sonner";

export function LoginCard({ role }: { role: "admin" | "teacher" }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError("Please enter your username and password.");
      return;
    }
    setLoading(true);
    await new Promise(r => setTimeout(r, 500));
    if (role === "admin") {
      if (
        (username === "admin" || username === "admin@rajnagar.edu") &&
        password === "admin123"
      ) {
        setAuth({ role: "admin", id: "ADMIN", name: "Super Admin" });
        toast.success("Welcome back, Admin");
        navigate({ to: "/admin" });
      } else {
        setError("Invalid admin credentials. Try admin / admin123");
      }
    } else {
      const teacher = getTeachers().find(
        t => (t.username === username || t.email === username) && t.active
      );
      if (teacher && password === "teacher123") {
        setAuth({ role: "teacher", id: teacher.id, name: teacher.name });
        toast.success(`Welcome, ${teacher.name}`);
        navigate({ to: "/teacher" });
      } else {
        setError("Invalid teacher credentials. Try meera / teacher123");
      }
    }
    setLoading(false);
  };

  const isAdmin = role === "admin";
  const Icon = isAdmin ? Shield : Users;

  const perks = isAdmin
    ? [
        "Assign and re-assign submissions to teachers",
        "Real-time Power BI style analytics",
        "Excel export & printable reports",
      ]
    : [
        "Review videos assigned to you",
        "Mark Ready for Reward or Not Proper",
        "Track your verification progress",
      ];

  const demoUser = isAdmin ? "admin" : "meera";
  const demoPass = isAdmin ? "admin123" : "teacher123";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      {/* Page backdrop — deep royal */}
      <div className="absolute inset-0 bg-[#0b1f4d]" />
      <div className="absolute inset-0 gradient-royal opacity-90" />
      <div className="absolute inset-0 rays-bg opacity-30" />

      <div className="relative grid w-full max-w-6xl overflow-hidden rounded-[1.75rem] bg-white shadow-royal lg:grid-cols-2">
        {/* ============ Left: Brand panel ============ */}
        <div className="relative hidden flex-col justify-between overflow-hidden bg-[#0f2a6b] p-10 text-white lg:flex">
          <div className="absolute inset-0 gradient-royal" />
          <div className="absolute inset-0 rays-bg opacity-25" />
          <div className="pointer-events-none absolute -right-16 top-1/3 h-64 w-64 rounded-full border border-[#f0c14d]/35" />
          <div className="pointer-events-none absolute -right-8 top-1/3 h-48 w-48 rounded-full border border-[#f0c14d]/20" />

          <div className="relative">
            <Link to="/" className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center overflow-hidden rounded-xl bg-white p-0.5 shadow-sm">
                <BrandLogo className="h-11 w-11" />
              </div>
              <div>
                <div className="text-guj text-sm font-bold leading-tight">
                  રાજનગર જ્ઞાનોત્સવ
                </div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#f0c14d]">
                  Season 2.0 · 2026
                </div>
              </div>
            </Link>
          </div>

          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#f0c14d]/50 bg-[#f0c14d]/10 px-3 py-1 text-xs font-medium text-[#f0c14d]">
              <Icon className="h-3.5 w-3.5" />
              {isAdmin ? "Super Admin Portal" : "Teacher Portal"}
            </div>
            <h2 className="mt-5 text-4xl font-bold leading-[1.1] tracking-tight text-white">
              {isAdmin ? (
                <>
                  Command the{" "}
                  <span className="text-[#f0c14d]">Gyanotsav</span> experience.
                </>
              ) : (
                <>
                  Verify with <span className="text-[#f0c14d]">confidence</span>{" "}
                  and care.
                </>
              )}
            </h2>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/80">
              {isAdmin
                ? "Assign submissions, monitor teachers, and celebrate the brightest entries — all in one elegant workspace."
                : "Review submissions assigned to you and help recognise students who truly shine."}
            </p>

            <ul className="mt-6 space-y-2.5">
              {perks.map(p => (
                <li
                  key={p}
                  className="flex items-start gap-2.5 text-sm text-white/90"
                >
                  <span className="mt-0.5 grid h-4 w-4 flex-shrink-0 place-items-center rounded-full border border-[#f0c14d]/70 text-[#f0c14d]">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative flex items-center justify-between border-t border-white/15 pt-4 text-xs text-white/65">
            <span>© {new Date().getFullYear()} Rajnagar Jain Shwe.</span>
            <span className="inline-flex items-center gap-1.5 text-[#f0c14d]">
              <Sparkles className="h-3 w-3" /> Share · Learn · Shine
            </span>
          </div>
        </div>

        {/* ============ Right: White form panel (design reference) ============ */}
        <div className="relative bg-white p-6 sm:p-10">
          <div className="mb-6 flex items-center justify-between lg:hidden">
            <Link to="/" className="flex items-center gap-2">
              <BrandLogo className="h-9 w-9" />
              <div className="text-guj text-sm font-bold text-[#1e3a8a]">
                રાજનગર જ્ઞાનોત્સવ 2.0
              </div>
            </Link>
          </div>

          <div className="mx-auto max-w-md">
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-[#1e3a8a]/20 bg-[#1e3a8a]/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#1e3a8a]">
              <Icon className="h-3 w-3" />{" "}
              {isAdmin ? "Super Admin" : "Teacher"} Login
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-[#0f172a]">
              Welcome <span className="text-[#1e3a8a]">back</span>
            </h1>
            <p className="mt-1.5 text-sm text-slate-500">
              Sign in to continue to your {isAdmin ? "admin" : "teacher"}{" "}
              dashboard.
            </p>

            {/* Demo credentials — soft gold cream box */}
            <div className="mt-5 rounded-xl border border-[#e8c547]/60 bg-[#fffbeb] p-3.5 text-xs">
              <div className="mb-1.5 flex items-center gap-1.5 font-semibold text-slate-800">
                <KeyRound className="h-3.5 w-3.5 text-[#d4a017]" /> Demo
                credentials
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-slate-700">
                <span>
                  <span className="text-slate-500">User:</span> {demoUser}
                </span>
                <span>
                  <span className="text-slate-500">Pass:</span> {demoPass}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setUsername(demoUser);
                  setPassword(demoPass);
                }}
                className="mt-2 inline-flex items-center gap-1 font-semibold text-[#1e3a8a] hover:underline"
              >
                Auto-fill credentials <ArrowRight className="h-3 w-3" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <Label
                  htmlFor="u"
                  className="text-xs font-semibold uppercase tracking-wider text-slate-500"
                >
                  Email or Username
                </Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="u"
                    placeholder={demoUser}
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    className="h-11 border-slate-200 bg-white pl-9 text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#1e3a8a]"
                  />
                </div>
              </div>
              <div>
                <Label
                  htmlFor="p"
                  className="text-xs font-semibold uppercase tracking-wider text-slate-500"
                >
                  Password
                </Label>
                <div className="relative mt-1.5">
                  <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="p"
                    type={show ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="h-11 border-slate-200 bg-white pr-10 pl-9 text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#1e3a8a]"
                  />
                  <button
                    type="button"
                    onClick={() => setShow(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 hover:text-[#1e3a8a]"
                    aria-label={show ? "Hide password" : "Show password"}
                  >
                    {show ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm text-slate-700">
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={remember}
                    onCheckedChange={v => setRemember(!!v)}
                  />
                  <span>Remember me</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="font-medium text-[#1e3a8a] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-600">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="group h-11 w-full bg-[#1e3a8a] text-white shadow-md hover:bg-[#1e3a8a]/90"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Sign in to {isAdmin ? "Admin" : "Teacher"} Panel
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                )}
              </Button>
            </form>

            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-[10px] uppercase tracking-widest text-slate-400">
                or
              </span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            <Link
              to={isAdmin ? "/login/teacher" : "/login/admin"}
              className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-sm transition-all hover:border-[#1e3a8a]/30 hover:bg-[#1e3a8a]/5"
            >
              <span className="flex items-center gap-2.5">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#1e3a8a]/10 text-[#1e3a8a]">
                  {isAdmin ? (
                    <Users className="h-4 w-4" />
                  ) : (
                    <Shield className="h-4 w-4" />
                  )}
                </span>
                <span>
                  <div className="font-semibold text-slate-900">
                    {isAdmin
                      ? "Continue as Teacher"
                      : "Continue as Super Admin"}
                  </div>
                  <div className="text-xs text-slate-500">
                    {isAdmin
                      ? "Verify assigned submissions"
                      : "Full management access"}
                  </div>
                </span>
              </span>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </Link>

            <div className="mt-6 text-center text-xs text-slate-500">
              <Link to="/" className="hover:text-[#1e3a8a]">
                ← Back to home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
